import type { FunctionComponent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useLaptopHelperItemSpec } from '../hooks/useLaptopHelperItemSpec'
import { QuestionScreen } from '../QuestionScreen/QuestionScreen'
import {
  budgetQuestionsAndFilters,
  osQuestionsAndFilters,
  usecaseQuestionsAndFilters,
} from './QuestionsConfig'

const questionConfigs = [
  budgetQuestionsAndFilters,
  osQuestionsAndFilters,
  usecaseQuestionsAndFilters,
]

export type LaptopHelperStep = {
  component: FunctionComponent
  trackingCallback?: (rest?: Record<string, string>) => void
  title?: string
}

export const useLaptopQuestionSteps = () => {
  const itemSpec = useLaptopHelperItemSpec()
  const [selectedIndexes, setSelectedIndexes] = useState<(number | undefined)[]>(
    Array(questionConfigs.length).fill(undefined)
  )

  const [filters, setFilters] = useState<Record<string, { [code: string]: string[] }>>({})

  const handleOptionSelect = useCallback((questionIndex: number, optionIndex: number) => {
    setSelectedIndexes((prevSelectedIndexes) => {
      const newSelectedIndexes = [...prevSelectedIndexes]
      newSelectedIndexes[questionIndex] = optionIndex
      return newSelectedIndexes
    })

    setFilters((prevFilters) => ({
      ...prevFilters,
      ...questionConfigs[questionIndex].options[optionIndex].filter,
    }))
  }, [])

  const steps = useMemo<LaptopHelperStep[]>(() => {
    return questionConfigs.map((config, index) => ({
      component: () => (
        <QuestionScreen
          question={config.question}
          options={config.options}
          screenName={`step ${index + 1}`}
          selectedIndex={selectedIndexes[index]}
          callback={(optionIndex: number) => handleOptionSelect(index, optionIndex)}
        />
      ),
      trackingCallback: (rest?: Record<string, string>) => {
        const optionIndex = selectedIndexes[index] ?? -1

        dynamicGa4DataLayerPush({
          event: 'personalisation_tool_question',
          action_group: 'question',
          item_index: optionIndex + 1,
          item_parent_text: `step ${index + 1}`,
          item_question: questionConfigs[index].shortQuestion,
          item_answer: questionConfigs[index].options?.[optionIndex]?.label ?? 'NO ANSWER SELECTED',
          item_spec: itemSpec,
          item_group: 'personalisation tools',
          tool_name: 'Laptop picker helper',
          item_personalisation: 'false',
          ...rest,
        })
      },
    }))
  }, [selectedIndexes, handleOptionSelect, itemSpec])

  return {
    steps,
    filters,
  }
}
